import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from 'types';
import { generateFormData } from 'utils';

import { apiBaseQuery } from 'modules/apiBaseQuery';

import {
  GetLocalePayload,
  IBrandingSettings,
  IFeatureSettings,
  IGetPlatformDocumentationPayload,
  ILocale,
  ILocaleNameSpace,
  IPlatformDocumentationResponse,
  ISearchPlatformDocumentation,
  ISearchPlatformDocumentationPayload,
  ImportLocalesPayload,
  SaveBrandingSettingsPayload,
  SaveFeatureSettingsPayload,
  SaveLocalesPayload,
  SaveProductDefinitionsPayload,
} from '../types/platform.types';

export const platformApi = createApi({
  reducerPath: 'platformApi',
  baseQuery: apiBaseQuery(`${process.env.REACT_APP_API_URL}/v2/api/admin`),
  endpoints: (builder) => ({
    getBrandingSettings: builder.query<ApiResponse<IBrandingSettings>, void>({
      query: () => ({
        url: '/platform-settings/branding',
        method: 'GET',
      }),
    }),
    saveBrandingSettings: builder.mutation<ApiResponse<IBrandingSettings>, SaveBrandingSettingsPayload>({
      query: (payload) => ({
        url: '/platform-settings/branding',
        method: 'PUT',
        body: payload,
      }),
    }),
    restoreBrandingSettings: builder.mutation<ApiResponse<IBrandingSettings>, void>({
      query: () => ({
        url: '/platform-settings/branding/restore',
        method: 'PUT',
      }),
    }),
    getFeatureSettings: builder.query<ApiResponse<IFeatureSettings>, void>({
      query: () => ({
        url: '/platform-settings/features',
        method: 'GET',
      }),
    }),
    saveFeatureSettings: builder.mutation<ApiResponse<IFeatureSettings>, SaveFeatureSettingsPayload>({
      query: (payload) => ({
        url: '/platform-settings/features',
        method: 'PUT',
        body: payload,
      }),
    }),
    saveProductDefinitions: builder.mutation<ApiResponse<IFeatureSettings>, SaveProductDefinitionsPayload>({
      query: (payload) => ({
        url: '/platform-settings/features',
        method: 'POST',
        body: payload,
      }),
    }),
    getPlatformDocumentation: builder.query<
      ApiResponse<IPlatformDocumentationResponse>,
      IGetPlatformDocumentationPayload
    >({
      query: (payload) => ({
        url: `/flow-docs${payload.url}`,
        method: 'GET',
      }),
    }),
    searchPlatformDocumentation: builder.query<
      ApiResponse<ISearchPlatformDocumentation[]>,
      ISearchPlatformDocumentationPayload
    >({
      query: (params) => ({
        url: `/flow-docs/search`,
        params,
        method: 'GET',
      }),
    }),

    getLocale: builder.query<ApiResponse<ILocale[]>, GetLocalePayload>({
      query: (params) => ({
        url: `/platform-settings/locales`,
        params,
        method: 'GET',
      }),
    }),

    exportLocales: builder.query<string, void>({
      query: () => ({
        url: `/platform-settings/locales/export`,
        method: 'GET',
      }),
    }),

    getLocaleNameSpaces: builder.query<ApiResponse<ILocaleNameSpace[]>, void>({
      query: () => ({
        url: `/platform-settings/locales/namespaces`,
        method: 'GET',
      }),
    }),

    importLocales: builder.mutation<void, ImportLocalesPayload>({
      query: (payload) => ({
        url: `/platform-settings/locales/import`,
        method: 'POST',
        body: generateFormData(payload),
      }),
    }),

    saveLocales: builder.mutation<void, SaveLocalesPayload>({
      query: (payload) => ({
        url: `/platform-settings/locales`,
        method: 'PUT',
        body: payload,
      }),
    }),
  }),
});

export const {
  useSaveFeatureSettingsMutation,
  useLazyGetFeatureSettingsQuery,
  useLazyGetPlatformDocumentationQuery,
  useLazySearchPlatformDocumentationQuery,
  useSaveBrandingSettingsMutation,
  useLazyGetBrandingSettingsQuery,
  useRestoreBrandingSettingsMutation,
  useLazyGetLocaleNameSpacesQuery,
  useLazyGetLocaleQuery,
  useLazyExportLocalesQuery,
  useImportLocalesMutation,
  useSaveLocalesMutation,
  useSaveProductDefinitionsMutation,
} = platformApi;
